/**
 * /news-preview, ESG World News bulletin (hidden URL during dev).
 *
 * Inherits the landing-page dark aesthetic. Paywalled, backend returns 402
 * if the user is not subscribed/grandfathered.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import {
  Newspaper, ArrowSquareOut, House, SignOut, Lightning, CalendarBlank,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_OPTS = [
  { v: "global", en: "Global",                ar: "عالمي" },
  { v: "eu",     en: "European Union",        ar: "الاتحاد الأوروبي" },
  { v: "us",     en: "United States",         ar: "الولايات المتحدة" },
  { v: "uk",     en: "United Kingdom",        ar: "المملكة المتحدة" },
  { v: "mena",   en: "MENA",                  ar: "الشرق الأوسط وشمال أفريقيا" },
  { v: "gcc",    en: "GCC (UAE focus)",       ar: "دول الخليج (الإمارات)" },
  { v: "apac",   en: "Asia-Pacific",          ar: "آسيا والمحيط الهادئ" },
  { v: "latam",  en: "Latin America",         ar: "أمريكا اللاتينية" },
  { v: "africa", en: "Sub-Saharan Africa",    ar: "أفريقيا جنوب الصحراء" },
];

const PILLAR_OPTS = [
  { v: "all",  en: "All pillars",             ar: "كل المحاور" },
  { v: "e",    en: "Environmental (E)",       ar: "البيئي (E)" },
  { v: "s",    en: "Social (S)",              ar: "الاجتماعي (S)" },
  { v: "g",    en: "Governance (G)",          ar: "الحوكمة (G)" },
  { v: "reg",  en: "Regulations & Standards", ar: "اللوائح والمعايير" },
  { v: "corp", en: "Corporate Initiatives",   ar: "مبادرات الشركات" },
];

const TIME_OPTS = [
  { v: "7",  en: "Last 7 days",  ar: "آخر 7 أيام" },
  { v: "30", en: "Last 30 days", ar: "آخر 30 يوم" },
  { v: "90", en: "Last 90 days", ar: "آخر 90 يوم" },
];

const PILLAR_COLOR = {
  E:    { bg: "rgba(1,175,155,0.15)",  fg: "#01AF9B" },
  S:    { bg: "rgba(185,75,5,0.18)",   fg: "#F97316" },
  G:    { bg: "rgba(30,58,95,0.40)",   fg: "#7DD3FC" },
  REG:  { bg: "rgba(220,38,38,0.18)",  fg: "#FCA5A5" },
  CORP: { bg: "rgba(139,92,246,0.18)", fg: "#C4B5FD" },
};

function Pill({ pillar }) {
  const meta = PILLAR_COLOR[pillar] || PILLAR_COLOR.CORP;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase"
          style={{ background: meta.bg, color: meta.fg }}>
      {pillar}
    </span>
  );
}

/**
 * Build a Google Translate proxy URL that auto-opens the source article
 * already translated to Arabic.
 *
 * We use Google's modern *.translate.goog subdomain proxy (used by Chrome's
 * built-in page translator). The legacy translate.google.com/translate?u=...
 * endpoint was deprecated in 2024 and now fails for many sites.
 *
 * Pattern:
 *   https://{host-with-dots-and-dashes-rewritten}.translate.goog{path}
 *     ?_x_tr_sl=auto   (source lang auto-detect)
 *     &_x_tr_tl=ar     (target = Arabic)
 *     &_x_tr_hl=ar     (Google Translate UI lang)
 *
 * Host rewrite rules:
 *   . → -        (dots become single dashes)
 *   - → --       (existing dashes get doubled so the inverse is recoverable)
 */
function buildArTranslateUrl(rawUrl) {
  try {
    const u = new URL(rawUrl);
    if (u.protocol !== "https:" && u.protocol !== "http:") return rawUrl;
    const encodedHost = u.hostname.replace(/-/g, "--").replace(/\./g, "-");
    const params = new URLSearchParams(u.search);
    params.set("_x_tr_sl", "auto");
    params.set("_x_tr_tl", "ar");
    params.set("_x_tr_hl", "ar");
    return `https://${encodedHost}.translate.goog${u.pathname}?${params.toString()}${u.hash || ""}`;
  } catch {
    return rawUrl;  // fall back to direct link if URL parsing fails
  }
}

export default function NewsPage() {
  const { user, logout } = useAuth();
  const { t, lang, isRtl } = useLang();
  const navigate = useNavigate();
  const T = (en, ar) => (lang === "ar" ? ar : en);

  const [region, setRegion] = useState("global");
  const [pillar, setPillar] = useState("all");
  const [timeWindow, setTimeWindow] = useState("7");
  const [busy, setBusy] = useState(false);
  const [bulletin, setBulletin] = useState(null);
  const [error, setError] = useState("");
  const [quota, setQuota] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("esg_token")}` } });

  const refreshQuota = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/news/quota`, auth());
      setQuota(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  // When user toggles language while a bulletin is showing, translate it
  // via the same background-job + polling pattern as initial generation.
  // This avoids the production load balancer's 30 s GET timeout that was
  // silently falling back to the original-language content.
  useEffect(() => {
    if (!bulletin?.id) return;
    if (bulletin.lang === lang) return;
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        setError("");
        // 1) Submit translation job
        const submit = await axios.post(
          `${API}/news/${bulletin.id}/translate`,
          { lang },
          { ...auth(), timeout: 15000 },
        );
        const jobId = submit?.data?.job_id;
        if (!jobId) throw new Error("Server did not return a job id");
        // 2) Poll every 2 s, max 5 min
        const MAX_POLLS = 150;
        const POLL_MS = 2000;
        for (let i = 0; i < MAX_POLLS; i++) {
          if (cancelled) return;
          await new Promise(res => setTimeout(res, POLL_MS));
          if (cancelled) return;
          const poll = await axios.get(
            `${API}/news/jobs/${jobId}`,
            { ...auth(), timeout: 20000 },
          );
          const s = poll?.data?.status;
          if (s === "done") {
            const b = poll.data.bulletin;
            if (!cancelled) setBulletin(prev => ({ ...prev, ...b, quota: prev?.quota }));
            return;
          }
          if (s === "failed") {
            throw new Error(poll.data.error || `Translation failed (HTTP ${poll.data.http_status})`);
          }
        }
        throw new Error(T(
          "Translation timed out after 5 minutes.",
          "انتهت مهلة الترجمة بعد 5 دقائق.",
        ));
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        const msg = detail || e?.message || T("Could not translate this bulletin.", "تعذّر ترجمة هذه النشرة.");
        setError(msg);
        console.error("[News] translate failed:", { detail, message: e?.message, response: e?.response });
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, bulletin?.id]);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setBulletin(null);
    try {
      // 1) Submit job, returns instantly with a job_id, no waiting on Claude.
      const submit = await axios.post(`${API}/news/generate`,
        { region, pillar, time_window: timeWindow, lang },
        { ...auth(), timeout: 15000 });
      const jobId = submit?.data?.job_id;
      if (!jobId) throw new Error("Server did not return a job id");

      // 2) Poll every 2 seconds, up to 5 minutes. AR takes ~45s, EN ~25s.
      const MAX_POLLS = 150;          // 150 × 2s = 5 minutes
      const POLL_MS = 2000;
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(res => setTimeout(res, POLL_MS));
        const poll = await axios.get(`${API}/news/jobs/${jobId}`,
          { ...auth(), timeout: 20000 });
        const s = poll?.data?.status;
        if (s === "done") {
          const b = poll.data.bulletin;
          setBulletin(b);
          setQuota(b?.quota);
          return;
        }
        if (s === "failed") {
          const httpStatus = poll.data.http_status;
          if (httpStatus === 402) {
            toast.error(T("Paid subscription required.", "اشتراك مدفوع مطلوب."));
            navigate("/billing");
            return;
          }
          throw new Error(poll.data.error || `Job failed (HTTP ${httpStatus})`);
        }
        // status === "pending" → keep polling
      }
      throw new Error(T(
        "Generation timed out after 5 minutes. Please try again with a narrower time window.",
        "انتهت مهلة الإنشاء بعد 5 دقائق. يُرجى المحاولة بنافذة زمنية أضيق."
      ));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(T("Paid subscription required.", "اشتراك مدفوع مطلوب."));
        navigate("/billing");
        return;
      }
      let msg;
      if (detail) {
        msg = detail;
      } else if (e?.message && !e.message.startsWith("Request failed")) {
        msg = e.message;
      } else if (status >= 500) {
        msg = T(`Server error (HTTP ${status}).`, `خطأ في الخادم (HTTP ${status}).`);
      } else if (status) {
        msg = `HTTP ${status}`;
      } else {
        msg = T("Could not generate bulletin.", "تعذّر إنشاء النشرة.");
      }
      setError(msg);
      console.error("[News] generate failed:", { status, detail, code: e?.code, message: e?.message, response: e?.response });
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-AE" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div className="min-h-screen bg-[#070709] text-slate-100" dir={isRtl ? "rtl" : "ltr"} data-testid="news-page">
      {/* Header, identical to Dashboard */}
      <header className="border-b border-slate-800/60 bg-[#0B0C10]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 min-w-0 group" data-testid="news-logo">
            <Logo size="md" />
          </button>
          <div className={`flex items-center gap-2 sm:gap-3 flex-shrink-0 ${isRtl ? "flex-row-reverse" : ""}`}>
            {user?.name && <span className="text-sm text-slate-300 hidden lg:inline">{user.name}</span>}
            <LangSwitch />
            <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-300 hover:text-white flex items-center gap-1 p-1" data-testid="news-home" title={T("Home", "الرئيسية")}>
              <House size={18} />
              <span className="hidden sm:inline">{T("Home", "الرئيسية")}</span>
            </button>
            <button onClick={() => navigate("/admin")} className="text-sm text-slate-300 hover:text-white p-1" data-testid="news-admin"
                    style={{display: user?.role === "admin" ? undefined : "none"}}
                    title="Admin">
              <span className="hidden sm:inline">{T("Admin", "المسؤول")}</span>
              <span className="sm:hidden font-bold">A</span>
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="text-sm text-slate-300 hover:text-white flex items-center gap-1 p-1" data-testid="news-logout" title={T("Logout", "تسجيل الخروج")}>
              <SignOut size={18} />
              <span className="hidden sm:inline">{T("Logout", "تسجيل الخروج")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="text-[#EAB308] text-xs font-bold tracking-[0.32em] uppercase mb-2 flex items-center gap-2">
          <Newspaper size={14} weight="fill" />
          {T("ESG WORLD NEWS", "أخبار ESG العالمية")}
        </div>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-3">
          {T("ESG Intelligence Bulletin", "نشرة استخبارات ESG")}
        </h1>
        <p className="text-slate-300 text-base sm:text-lg max-w-2xl mb-8">
          {T(
            "Real, sourced news from regulators, advisory firms, and global media, refined by AI.",
            "أخبار حقيقية موثّقة من الجهات التنظيمية والشركات الاستشارية والإعلام العالمي، يصقلها الذكاء الاصطناعي."
          )}
        </p>

        {/* Configuration panel */}
        <section className="bg-[#111216] border border-slate-800/60 rounded-2xl overflow-hidden mb-8" data-testid="news-config">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-800/60">
            <Field label={T("Region", "المنطقة")}>
              <select value={region} onChange={e => setRegion(e.target.value)} className={selectCls} data-testid="news-region">
                {REGION_OPTS.map(o => <option key={o.v} value={o.v}>{T(o.en, o.ar)}</option>)}
              </select>
            </Field>
            <Field label={T("ESG Pillar", "محور ESG")}>
              <select value={pillar} onChange={e => setPillar(e.target.value)} className={selectCls} data-testid="news-pillar">
                {PILLAR_OPTS.map(o => <option key={o.v} value={o.v}>{T(o.en, o.ar)}</option>)}
              </select>
            </Field>
            <Field label={T("Time window", "الفترة الزمنية")}>
              <select value={timeWindow} onChange={e => setTimeWindow(e.target.value)} className={selectCls} data-testid="news-window">
                {TIME_OPTS.map(o => <option key={o.v} value={o.v}>{T(o.en, o.ar)}</option>)}
              </select>
            </Field>
          </div>
          <div className="px-5 py-4 border-t border-slate-800/60 flex flex-wrap items-center justify-between gap-3">
            <button onClick={generate} disabled={busy} data-testid="news-generate-btn"
                    className="bg-[#01AF9B] hover:bg-[#018F7E] disabled:opacity-60 disabled:cursor-wait text-white font-['Outfit'] font-bold py-3 px-6 rounded-xl transition-all inline-flex items-center gap-2">
              <Lightning size={18} weight="fill" />
              {busy ? T("Scanning global ESG feeds…", "جارٍ مسح مصادر ESG…") : T("Generate Bulletin", "إنشاء النشرة")}
            </button>
            {quota && (
              <span className="text-xs text-slate-400" data-testid="news-quota">
                {quota.limit >= 999
                  ? T("Unlimited", "غير محدود")
                  : T(
                      `Generations remaining today: ${quota.remaining} / ${quota.limit}`,
                      `محاولات متبقية اليوم: ${quota.remaining} / ${quota.limit}`
                    )}
              </span>
            )}
          </div>
        </section>

        {/* Loading / error / bulletin */}
        {busy && (
          <BusyPanel lang={lang} T={T} />
        )}

        {error && !busy && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-200 rounded-2xl p-6 mb-6" data-testid="news-error">
            {error}
          </div>
        )}

        {bulletin && !busy && (
          <article className="bg-[#111216] border border-slate-800/60 rounded-2xl overflow-hidden mb-8" data-testid="news-bulletin">
            <div className="bg-[#0B0C10] border-b border-slate-800/60 p-6 sm:p-8">
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white leading-tight">
                {bulletin.bulletin_title}
              </h2>
              <p className="text-slate-400 mt-2 text-sm sm:text-base">{bulletin.subtitle}</p>
              <div className="flex flex-wrap gap-4 mt-4 text-xs text-slate-500">
                <span>{fmtDate(bulletin.generated_at)}</span>
                <span>·</span>
                <span>{bulletin.item_count} {T("items", "عنصر")}</span>
              </div>
            </div>
            <div className="p-6 sm:p-8 space-y-8">
              {(bulletin.sections || []).map((s, i) => (
                <section key={i} data-testid={`news-section-${i}`}>
                  <h3 className="font-['Outfit'] text-[#01AF9B] text-[11px] font-bold tracking-[0.18em] uppercase border-b-2 border-[#01AF9B]/30 pb-1.5 mb-4">
                    {s.title}
                  </h3>
                  <div className="space-y-5">
                    {s.items.map((it, j) => {
                      const linkUrl = lang === "ar" ? buildArTranslateUrl(it.url) : it.url;
                      return (
                        <a key={j} href={linkUrl} target="_blank" rel="noopener noreferrer"
                           className="block group p-4 rounded-lg border border-transparent hover:border-slate-700 hover:bg-white/[0.02] transition-all"
                           data-testid={`news-item-${i}-${j}`}>
                          <div className="flex items-start gap-2">
                            <h4 className="text-white font-bold text-[15px] leading-snug flex-1 group-hover:text-[#F97316] transition-colors"
                                title={it.title_original || it.title}>
                              {it.title}
                            </h4>
                            <ArrowSquareOut size={14} className="text-slate-500 group-hover:text-[#F97316] mt-1 flex-shrink-0" />
                          </div>
                          {lang === "ar" && it.title_original && it.title_original !== it.title && (
                            <div className="text-[11px] text-slate-500 mt-1 leading-snug italic" dir="ltr">{it.title_original}</div>
                          )}
                          <p className="text-sm text-slate-400 mt-2 leading-relaxed">{it.summary}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-slate-500">
                            <span className="font-semibold text-slate-300">{it.source_name}</span>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1"><CalendarBlank size={11} />{fmtDate(it.published_date)}</span>
                            <Pill pillar={it.pillar} />
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
            <div className="bg-[#0B0C10] border-t border-slate-800/60 px-6 sm:px-8 py-4 text-[11px] text-slate-500 leading-relaxed">
              {T(
                "News compiled by AI from public sources. Always verify by clicking the source link before citing in formal documents.",
                "أخبار مُجمّعة بواسطة الذكاء الاصطناعي من مصادر عامة. تحقّق دائماً عبر رابط المصدر قبل الاستشهاد في الوثائق الرسمية."
              )}
            </div>
          </article>
        )}

      </main>
    </div>
  );
}

const selectCls = "w-full bg-[#0B0C10] border border-slate-700 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-[#01AF9B] transition-colors";

function Field({ label, children }) {
  return (
    <div className="p-5">
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{label}</label>
      {children}
    </div>
  );
}



function BusyPanel({ lang, T }) {
  // Live elapsed-seconds counter so users see progress instead of a frozen spinner.
  // AR generation takes ~45s, EN ~25s; this prevents the "is it broken?" anxiety.
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const target = lang === "ar" ? 50 : 30;
  const pct = Math.min(95, Math.round((secs / target) * 100));
  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-10 text-center" data-testid="news-loading">
      <div className="w-10 h-10 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin mx-auto mb-5" />
      <div className="font-['Outfit'] text-lg font-bold text-white mb-2">
        {T("Generating bulletin…", "جارٍ إنشاء النشرة…")}
      </div>
      <div className="text-slate-400 text-sm mb-5">
        {secs}s · {T(`~${target}s expected`, `يستغرق ~${target} ثانية`)}
      </div>
      <div className="max-w-xs mx-auto h-1.5 rounded-full bg-[#0B0C10] overflow-hidden border border-slate-800 mb-5">
        <div className="h-full bg-[#01AF9B] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <ul className="text-xs text-slate-500 space-y-1">
        <li>· {T("Scanning regulators & global media", "مسح الجهات التنظيمية والإعلام العالمي")}</li>
        <li>· {T("Filtering by your criteria", "تصفية حسب معاييرك")}</li>
        <li>· {T("Cross-checking sources", "مطابقة المصادر")}</li>
        <li>· {T("Drafting summary", "صياغة الملخّص")}</li>
      </ul>
    </div>
  );
}
